import { Link } from "@tanstack/react-router";
import {
  BarChart3,
  BookOpen,
  ChevronRight,
  CreditCard,
  ExternalLink,
  MessageSquare,
  Shield,
  Users,
  Zap,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";

export interface HelpQuickLink {
  icon: ReactNode;
  label: string;
  desc: string;
  href: string;
}

export interface HelpCategoryItem {
  label: string;
  slug: string;
  desc: string;
}

export interface HelpCategory {
  title: string;
  icon: ReactNode;
  color: string;
  items: HelpCategoryItem[];
}

export const HELP_QUICK_LINKS: HelpQuickLink[] = [
  {
    icon: <MessageSquare className="size-4" />,
    label: "1:1 문의",
    desc: "카카오톡 오픈채팅으로 문의",
    href: "https://modoo.today/talk",
  },
  {
    icon: <BookOpen className="size-4" />,
    label: "API 문서",
    desc: "개발자 문서 및 API 레퍼런스",
    href: "/docs",
  },
  {
    icon: <Users className="size-4" />,
    label: "커뮤니티",
    desc: "마케터 스터디 오픈채팅방",
    href: "https://modoo.today/study",
  },
];

export const HELP_CATEGORIES: HelpCategory[] = [
  {
    title: "시작하기",
    icon: <Zap className="size-4" />,
    color: "bg-amber-50 text-amber-600",
    items: [
      {
        label: "서비스 소개",
        slug: "getting-started",
        desc: "데이터랩툴즈의 핵심 기능과 사용 방법을 알아보세요",
      },
      {
        label: "회원가입 & 로그인",
        slug: "account",
        desc: "계정 생성, 이메일 인증, 소셜 로그인 안내",
      },
      {
        label: "첫 키워드 등록하기",
        slug: "first-keyword",
        desc: "키워드를 등록하고 분석 결과를 확인하는 방법",
      },
    ],
  },
  {
    title: "이용권 & 결제",
    icon: <CreditCard className="size-4" />,
    color: "bg-sky-50 text-sky-600",
    items: [
      {
        label: "이용권 안내",
        slug: "usage",
        desc: "플랜별 기능 비교, API 호출 제한, 이용 기간 안내",
      },
      {
        label: "이용권 등록 방법",
        slug: "license-register",
        desc: "스마트스토어 주문번호로 이용권을 등록하는 방법",
      },
      { label: "환불 정책", slug: "refund", desc: "환불 조건 및 절차 안내" },
    ],
  },
  {
    title: "파티 시스템",
    icon: <Users className="size-4" />,
    color: "bg-violet-50 text-violet-600",
    items: [
      {
        label: "파티란?",
        slug: "party",
        desc: "파티 시스템의 개념과 혜택을 알아보세요",
      },
      {
        label: "파티 가입 & 초대",
        slug: "party-join",
        desc: "파티에 가입하거나 멤버를 초대하는 방법",
      },
      {
        label: "파티 보상 체계",
        slug: "party-rewards",
        desc: "파티 활동에 따른 보상과 혜택 안내",
      },
    ],
  },
  {
    title: "기능 가이드",
    icon: <BarChart3 className="size-4" />,
    color: "bg-emerald-50 text-emerald-600",
    items: [
      {
        label: "키워드 분석",
        slug: "guide-keywords",
        desc: "검색량, 경쟁도, 트렌드 분석 가이드",
      },
      {
        label: "채널 분석",
        slug: "guide-channels",
        desc: "블로그·인플루언서 채널 진단 가이드",
      },
      {
        label: "AI 기능",
        slug: "guide-ai",
        desc: "클러스터링, 의도 분석, 글 구조 제안 등 AI 기능 가이드",
      },
      {
        label: "검색광고",
        slug: "guide-ads",
        desc: "입찰가 분석, ROI 시뮬레이션 가이드",
      },
    ],
  },
  {
    title: "약관 & 정책",
    icon: <Shield className="size-4" />,
    color: "bg-gray-50 text-gray-600",
    items: [
      { label: "이용약관", slug: "terms", desc: "서비스 이용약관 전문" },
      {
        label: "개인정보 처리방침",
        slug: "privacy",
        desc: "개인정보 수집·이용·제공에 관한 방침",
      },
      {
        label: "API 사용 정책",
        slug: "api-policy",
        desc: "API 호출 제한, 남용 방지 정책",
      },
    ],
  },
];

export function HelpQuickLinks({
  links = HELP_QUICK_LINKS,
}: {
  links?: HelpQuickLink[];
}) {
  return (
    <div className="mb-8 grid gap-3 sm:grid-cols-3">
      {links.map((link) => {
        const isExternal = link.href.startsWith("http");
        return (
          <a
            key={link.label}
            href={link.href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            className="group flex items-center gap-3.5 rounded-xl border border-stroke-subtle bg-surface-raised p-4 transition-all hover:shadow-md"
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-text-brand">
              {link.icon}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold">{link.label}</p>
              <p className="text-[11px] text-text-tertiary">{link.desc}</p>
            </div>
            <ExternalLink className="size-3.5 shrink-0 text-text-tertiary opacity-0 transition-opacity group-hover:opacity-60" />
          </a>
        );
      })}
    </div>
  );
}

export function HelpCategoryGrid({
  categories = HELP_CATEGORIES,
}: {
  categories?: HelpCategory[];
}) {
  return (
    <div className="space-y-6">
      {categories.map((category) => (
        <section key={category.title}>
          <div className="mb-3 flex items-center gap-2">
            <div
              className={cn(
                "flex size-6 items-center justify-center rounded-md",
                category.color,
              )}
            >
              {category.icon}
            </div>
            <h2 className="text-[14px] font-bold">{category.title}</h2>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {category.items.map((item) => (
              <Link
                key={item.slug}
                to="/docs/$"
                params={{ _splat: item.slug }}
                className="group flex items-center justify-between rounded-xl border border-stroke-subtle bg-surface-raised px-4 py-3.5 transition-all hover:shadow-sm"
              >
                <div>
                  <p className="text-[13px] font-semibold">{item.label}</p>
                  <p className="mt-0.5 text-[11px] text-text-tertiary">
                    {item.desc}
                  </p>
                </div>
                <ChevronRight className="size-3.5 shrink-0 text-text-tertiary opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-60" />
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
